/**
 * AetherLite AI — cliente de los endpoints /aetherlite/ai/*
 *
 * Backend: aethercoach-mvp/backend/routers/aetherlite_ai.py
 *
 * Cuotas mensuales por tier (libreta servidor — el front solo las muestra):
 *   free=0, solo=2, studio=5, unlimited=15
 *
 * Reset: primer día del mes calendario UTC. Errores NO consumen cuota.
 */
import { apiFetch } from "./client";

export interface AiQuota {
  tier: string;
  limit: number;
  used: number;
  remaining: number;
  resets_at: string;       // ISO datetime
}

/** Enum cerrado para el equipamiento del cliente. Mapea en backend a un set
 *  de valores reales del campo `exercise_library.equipment` (EQUIPMENT_KIND_MAP).
 *  - gym: gym completo (todo el catálogo)
 *  - gym_residencial: gym de conjunto/hotel — mancuernas livianas + 1 multifuncional (poleas/máquina) + corporal
 *  - casa_mancuernas: mancuerna + bandas + corporal + kettlebell + pelotas
 *  - casa_peso_corporal: solo corporal (calistenia pura)
 *  - crossfit: kettlebells + trineo + bandas + cuerda + funcional
 */
export type AiEquipmentKind = "gym" | "gym_residencial" | "casa_mancuernas" | "casa_peso_corporal" | "crossfit";

/** Grupos musculares que el coach puede asignar por día (modo "personalizar
 *  por día"). Deben matchear MUSCLE_GROUP_MAP del backend. */
export const MUSCLE_GROUPS = [
  "Pecho", "Espalda", "Hombro", "Bíceps", "Tríceps",
  "Cuádriceps", "Isquios", "Glúteo", "Pantorrilla",
  "Abdomen", "Antebrazo", "Cardio",
] as const;

/** Días de la semana en orden — deben matchear WEEK_DAYS del backend. */
export const WEEK_DAYS = [
  "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo",
] as const;

export interface AiDayScheduleEntry {
  day: string;
  muscles: string[]; // [] = descanso
}

export interface AiGeneratePlanInput {
  client_id: number;
  plan_id?: number | null;
  days_per_week: number;    // 1-7
  focus?: string | null;    // "Glúteo + pierna", "Pecho + espalda", etc.
  /** Modo "personalizar por día": qué grupos trabaja cada día (muscles=[] = descanso).
   *  Si viene, manda sobre days_per_week/focus. */
  day_schedule?: AiDayScheduleEntry[] | null;
  /** Ejercicios por grupo muscular en modo por-día (1-8). */
  exercises_per_group?: number;
  equipment_kind?: AiEquipmentKind;
  weeks: number;            // 1-12
  preferences?: string | null;
  allergies?: string | null;
  /** IDs de food_library que el coach pre-seleccionó como prioritarios. */
  preferred_food_ids?: number[];
  /** Si false, el modelo NO genera routine ni cardio (devuelve arrays vacíos).
   *  Default true en frontend. Si ambos false, el backend devuelve 400. */
  include_training?: boolean;
  /** Si false, el modelo NO genera diet ni supplements. Default true. */
  include_nutrition?: boolean;
  target_kcal?: number | null;
  target_protein?: number | null;
  target_carbs?: number | null;
  target_fat?: number | null;
  /** Texto libre del coach con adaptaciones/consideraciones (ej. "sin trabajo
   *  de piso, cuidar la rodilla"). Se inyecta al prompt. */
  special_considerations?: string | null;
  /** Fuerza el modo bajo impacto (excluye pliometría/equilibrio del catálogo).
   *  null/undefined = el backend lo deriva del IMC del cliente (≥30 → on). */
  low_impact?: boolean | null;
}

export interface AiGeneratePlanResult {
  /** Objeto PlanDoc-like generado por el modelo. Se mapea a sections del
   *  plan en el frontend antes de aplicar. Shape parcial — el coach decide
   *  si lo persiste o lo descarta. */
  plan_doc: any;
  tokens_in: number;
  tokens_out: number;
  tokens_cache_read: number;
  cost_usd: number;
  duration_ms: number;
  /** Cuántas generaciones quedan en el mes después de esta exitosa. */
  remaining_quota: number;
}

export async function getAiQuota(): Promise<AiQuota> {
  return apiFetch<AiQuota>("/aetherlite/ai/quota");
}

export async function generatePlanWithAi(input: AiGeneratePlanInput): Promise<AiGeneratePlanResult> {
  return apiFetch<AiGeneratePlanResult>("/aetherlite/ai/generate-plan", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
